import React, { useEffect, useState, useRef } from 'react';
import './MainPage.css';
import './AdvancedAnimations.css';
import logo from '../../images/logo.png';
import noRafflesImage from '../../images/no-raffles.png';
import { getVersionedApiUrl } from '../url';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faArrowRight, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditions';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import SideMenu from '../SideMenu/SideMenu';
import Footer from '../Footer/Footer';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

export default function MainPage() {
    const [rifas, setRifas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [contactos, setContactos] = useState([]);
    const [numerosEspeciales, setNumerosEspeciales] = useState({
        numero1: '',
        numero2: '',
        numero3: '',
        numero4: '',
        numero5: '',
        seccion_activa: false,
        mensaje: 'Si te sale alguno de nuestros números premiados, reclama de inmediato $50 a tasa BCV directo a tu cuenta bancaria.'
    });
    const [numerosEspecialesLoaded, setNumerosEspecialesLoaded] = useState(false);
    const [currentRifaIndex, setCurrentRifaIndex] = useState(0);
    const [showConfetti, setShowConfetti] = useState(true);
    const [windowDimensions, setWindowDimensions] = useState({ width: 0, height: 0 });
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const termsModalRef = useRef(null);
    const navigate = useNavigate();
    
    // Intersection observers for scroll animations
    const [footerRef, isFooterVisible] = useIntersectionObserver();
    const [specialNumbersRef, isSpecialNumbersVisible] = useIntersectionObserver();
    
    // Particle effect state
    const [particles, setParticles] = useState([]);

    useEffect(() => {
        cargarRifas();
        cargarContacto();
        cargarNumerosEspeciales();
        
        // Set window dimensions for confetti
        const updateWindowDimensions = () => {
            setWindowDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        
        updateWindowDimensions();
        window.addEventListener('resize', updateWindowDimensions);
        
        // Hide confetti after 6 seconds
        const confettiTimer = setTimeout(() => {
            setShowConfetti(false);
        }, 6000);
        
        // Create floating particles
        const createParticles = () => {
            const newParticles = [];
            for (let i = 0; i < 15; i++) {
                newParticles.push({
                    id: i,
                    x: Math.random() * window.innerWidth,
                    y: Math.random() * window.innerHeight,
                    delay: Math.random() * 4,
                    size: Math.random() * 4 + 2
                });
            }
            setParticles(newParticles);
        };

        createParticles();

        // Keyboard navigation for desktop/tablet
        const handleKeyDown = (e) => {
            if (window.innerWidth >= 768 && rifas.length > 1) {
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    handlePreviousRifa();
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    handleNextRifa();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('resize', updateWindowDimensions);
            window.removeEventListener('keydown', handleKeyDown);
            clearTimeout(confettiTimer);
        };
    }, [currentRifaIndex, rifas.length]);

    const cargarRifas = () => {
        fetch(getVersionedApiUrl('rifaGet.php'), {
            method: 'GET',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                setRifas(data.rifas || []);
                setLoading(false);
            })
            .catch(error => {
                setLoading(false);
            });
    };

    const cargarContacto = () => {
        fetch(getVersionedApiUrl('contactoGet.php'), {
            method: 'GET',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            }
        })
            .then(response => response.json())
            .then(data => {
                setContactos(data.contacto?.reverse()[0] || []);
            })
            .catch(error => {});
    };

    const cargarNumerosEspeciales = () => {
        fetch(getVersionedApiUrl('numerosEspecialesGet.php'), {
            method: 'GET',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                if (data.success && data.data) {
                    setNumerosEspeciales({
                        numero1: data.data.numero1 || '',
                        numero2: data.data.numero2 || '',
                        numero3: data.data.numero3 || '',
                        numero4: data.data.numero4 || '',
                        numero5: data.data.numero5 || '',
                        seccion_activa: data.data.seccion_activa == 1,
                        mensaje: data.data.mensaje || 'Si te sale alguno de nuestros números premiados, reclama de inmediato $50 a tasa BCV directo a tu cuenta bancaria.'
                    });
                } else {
                    // Si no hay datos válidos, mantener la sección desactivada
                    setNumerosEspeciales(prev => ({
                        ...prev,
                        seccion_activa: false
                    }));
                }
                setNumerosEspecialesLoaded(true);
            })
            .catch(error => {
                // En caso de error, mantener la sección desactivada
                setNumerosEspeciales(prev => ({
                    ...prev,
                    seccion_activa: false
                }));
                setNumerosEspecialesLoaded(true);
            });
    };

    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
            .trim();
    };

    const handleComprarAhora = (rifa) => {
        const slug = generateSlug(rifa.nombre);
        navigate(`/rifa/${rifa.idRifa}/${slug}`);
    };

    const handleSwipe = (direction) => {
        if (direction === 'left' && currentRifaIndex < rifas.length - 1) {
            setCurrentRifaIndex(currentRifaIndex + 1);
        } else if (direction === 'right' && currentRifaIndex > 0) {
            setCurrentRifaIndex(currentRifaIndex - 1);
        }
    };

    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        setTouchStart(touch.clientX);
    };

    const handleTouchEnd = (e) => {
        const touch = e.changedTouches[0];
        const touchEnd = touch.clientX;
        const diff = touchStart - touchEnd;
        
        if (Math.abs(diff) > 50) {
            if (diff > 0) {
                handleSwipe('left');
            } else {
                handleSwipe('right');
            }
        }
    };

    const [touchStart, setTouchStart] = useState(0);

    const handlePreviousRifa = () => {
        if (currentRifaIndex > 0) {
            setCurrentRifaIndex(currentRifaIndex - 1);
        }
    };

    const handleNextRifa = () => {
        if (currentRifaIndex < rifas.length - 1) {
            setCurrentRifaIndex(currentRifaIndex + 1);
        }
    };

    const handleTermsButtonClick = () => {
        if (termsModalRef.current) {
            termsModalRef.current.openModal();
        }
    };

    const currentRifa = rifas[currentRifaIndex];
    
    return (
        <div className="main-page">
            {/* Confetti Effect */}
            {showConfetti && (
                <Confetti
                    width={windowDimensions.width}
                    height={windowDimensions.height}
                    recycle={false}
                    numberOfPieces={150}
                    gravity={0.2}
                    initialVelocityY={15}
                    wind={0.02}
                    colors={['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8']}
                />
            )}

            {/* Floating Particles */}
            {particles.map(particle => (
                <div
                    key={particle.id}
                    className="particle"
                    style={{
                        left: particle.x,
                        top: particle.y,
                        width: particle.size,
                        height: particle.size,
                        animationDelay: `${particle.delay}s`
                    }}
                />
            ))}
            
            {/* Header */}
            <header className="main-header">
                <div className="header-left">
                    <img 
                        src={logo} 
                        alt="Logo" 
                        className="header-logo" 
                        onClick={() => navigate('/')}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
                <div className="header-center">
                    {/* Espacio para centrar el header */}
                </div>
                <div className="header-right">
                    <button className="menu-button" onClick={() => setIsMenuOpen(true)}>
                        <FontAwesomeIcon icon={faBars} />
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="main-content">
                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Cargando rifas...</p>
                    </div>
                ) : rifas.length > 0 ? (
                    <>
                        {/* Main Raffles Section */}
                        <div className="raffles-container">
                            {/* Section Title */}
                            <div className="section-title">
                                <h1>Rifas Activas</h1>
                            </div>
                            
                            {/* Raffle Card */}
                            <div 
                                className="raffle-card"
                                onTouchStart={handleTouchStart}
                                onTouchEnd={handleTouchEnd}
                            >
                                {currentRifa && (
                                    <>
                                        <div className="raffle-image">
                                            <img 
                                                src={currentRifa.imagen1} 
                                                alt={currentRifa.nombre}
                                                onError={(e) => {
                                                    e.target.src = noRafflesImage;
                                                }}
                                            />
                                        </div>
                                        
                                        <div className="prize-section">
                                            <h2>{currentRifa.nombre}</h2>
                                            <p>Fecha Límite: {currentRifa.finalizacion}.</p>
                                        </div>

                                        <div className="ticket-cost">
                                            <span className="cost-label">Costo Ticket</span>
                                            <span className="cost-value">{currentRifa.moneda} {currentRifa.precio}</span>
                                        </div>

                                        <div className="progress-section">
                                            <span className="progress-label">Tickets Reservados</span>
                                            <div className="progress-bar">
                                                <div 
                                                    className="progress-fill" 
                                                    style={{ width: `${currentRifa.porcentaje_vendido || 0}%` }}
                                                ></div>
                                            </div>
                                            <span className="progress-percentage">
                                                {currentRifa.porcentaje_vendido || 0}%
                                            </span>
                                        </div>

                                        <button 
                                            className="buy-button"
                                            onClick={() => handleComprarAhora(rifas[currentRifaIndex])}
                                        >
                                            Comprar ahora
                                            <FontAwesomeIcon icon={faArrowRight} />
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Desktop/Tablet Navigation Buttons */}
                            {rifas.length > 1 && (
                                <div className="raffle-navigation">
                                    <button 
                                        className="nav-button"
                                        onClick={handlePreviousRifa}
                                        disabled={currentRifaIndex === 0}
                                        aria-label="Rifa anterior"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    
                                    <div className="raffle-counter">
                                        <span className="current-raffle">
                                            {currentRifaIndex + 1} / {rifas.length}
                                        </span>
                                    </div>
                                    
                                    <button 
                                        className="nav-button"
                                        onClick={handleNextRifa}
                                        disabled={currentRifaIndex === rifas.length - 1}
                                        aria-label="Siguiente rifa"
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                            )}

                            {/* Mobile Swipe Indicators */}
                            {rifas.length > 1 && (
                                <div className="swipe-indicators">
                                    {rifas.map((_, index) => (
                                        <div 
                                            key={index}
                                            className={`indicator ${index === currentRifaIndex ? 'active' : ''}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Desktop Sidebar */}
                        <div className="desktop-sidebar-content">
                            {/* Special Numbers Section */}
                            {numerosEspeciales.seccion_activa && numerosEspecialesLoaded && (
                                <div 
                                    ref={specialNumbersRef}
                                    className={`special-numbers-section ${isSpecialNumbersVisible ? 'animate-in' : ''}`}
                                >
                                    <div className="special-info">
                                        <p>{numerosEspeciales.mensaje}</p>
                                    </div>
                                    
                                    <div className="special-numbers stagger-animation">
                                        <div className="number-row">
                                            <span className="special-number magnetic gradient-text">{numerosEspeciales.numero1}</span>
                                            <span className="special-number magnetic gradient-text">{numerosEspeciales.numero2}</span>
                                        </div>
                                        <div className="number-row">
                                            <span className="special-number magnetic gradient-text">{numerosEspeciales.numero3}</span>
                                            <span className="special-number magnetic gradient-text">{numerosEspeciales.numero4}</span>
                                            <span className="special-number magnetic gradient-text">{numerosEspeciales.numero5}</span>
                                        </div>
                                    </div>

                                </div>
                            )}
                        </div>

                        {/* Action Buttons - Moved outside desktop-sidebar-content */}
                        <div className="action-buttons stagger-animation">
                            <button className="action-button morph-button" onClick={() => navigate('/verificar-tickets')}>
                                Verificar Tickets
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                            <button className="action-button morph-button" onClick={handleTermsButtonClick}>
                                Términos y Condiciones
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="no-raffles">
                        <div className="no-raffles-content">
                            {/* Section Title */}
                            <div className="section-title">
                                <h1>Rifas Activas</h1>
                            </div>
                            <div className="no-raffles-image-container">
                                <div className="golden-circle-background"></div>
                                <img src={noRafflesImage} alt="No hay rifas disponibles" className="no-raffles-image" />
                            </div>
                            <h2 className="no-raffles-main-text">¡Estamos preparando algo increíble!</h2>
                            <p className="no-raffles-sub-text">Vuelve pronto, la próxima rifa está casi lista.</p>
                        </div>
                        
                        {/* Special Numbers Section for No Raffles */}
                        {numerosEspeciales.seccion_activa && numerosEspecialesLoaded && (
                            <div 
                                ref={specialNumbersRef}
                                className={`special-numbers-section ${isSpecialNumbersVisible ? 'animate-in' : ''}`}
                            >
                                <div className="special-info">
                                    <p>{numerosEspeciales.mensaje}</p>
                                </div>
                                
                                <div className="special-numbers stagger-animation">
                                    <div className="number-row">
                                        <span className="special-number magnetic gradient-text">{numerosEspeciales.numero1}</span>
                                        <span className="special-number magnetic gradient-text">{numerosEspeciales.numero2}</span>
                                    </div>
                                    <div className="number-row">
                                        <span className="special-number magnetic gradient-text">{numerosEspeciales.numero3}</span>
                                        <span className="special-number magnetic gradient-text">{numerosEspeciales.numero4}</span>
                                        <span className="special-number magnetic gradient-text">{numerosEspeciales.numero5}</span>
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* Action Buttons - Moved outside desktop-sidebar-content */}
                        <div className="action-buttons stagger-animation">
                            <button className="action-button morph-button" onClick={() => navigate('/verificar-tickets')}>
                                Verificar Tickets
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                            <button className="action-button morph-button" onClick={handleTermsButtonClick}>
                                Términos y Condiciones
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        </div>
                    </div>
                )}
            </main>

            <Footer ref={footerRef} className={`footer-animated ${isFooterVisible ? 'animate-in' : ''}`} />
            
            {/* Modal de Términos y Condiciones */}
            <TermsAndConditions ref={termsModalRef} />

            {/* Menú lateral */}
            <SideMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
        </div>
    );
}
